import React, { useMemo } from "react"
import classNames from "classnames"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { useAppSelector } from "../../../hooks"
import { GlobalState } from "../../../store"
import { IMessage } from ".."
import { DateTime } from "luxon"

export interface Props {
    id: string
}

export default function Messages({ id }: Props): JSX.Element {
    const aiModalData = useAppSelector(
        (state: GlobalState) => state.aIModal.modals[id],
    )
    if (!aiModalData) {
        return <></>
    }

    const { messages, isLoading, suggestedActions, labels } = aiModalData

    if (messages.length === 0) {
        return <></>
    }
    const baseClasses = "tw-px-3 tw-py-2 tw-whitespace-pre-line theme-bubble "

    // we need to sort the messages descending
    // newest should be shown on top
    const sortedMessages = useMemo(() => {
        return [...messages].sort((a: IMessage, b: IMessage) => {
            const aTimestamp = DateTime.fromISO(a.timestamp)
            const bTimestamp = DateTime.fromISO(b.timestamp)

            return aTimestamp > bTimestamp
                ? -1
                : aTimestamp < bTimestamp
                ? 1
                : 0
        })
    }, [messages])

    return (
        <div
            className="tw-flex tw-flex-col tw-items-start tw-gap-2 tw-px-2"
            data-ft-testid="messages"
        >
            {isLoading && (
                <div
                    className={`${baseClasses} theme-note is-loading`}
                    data-ft-testid="isLoading"
                >
                    {Symbols.EllipsisHorizontal}
                </div>
            )}

            {!isLoading && suggestedActions.length === 0 && (
                <div
                    className={`${baseClasses} theme-note`}
                    data-ft-testid="message"
                    data-ft-testid-type="noResult"
                >
                    {labels.noResult}
                </div>
            )}

            {sortedMessages.map((message, index) => {
                const classes = classNames(baseClasses, {
                    "bg-cloud": message.isSentByUser,
                    "theme-note": !message.isSentByUser,
                })

                return (
                    <div
                        className={classes}
                        data-ft-testid="message"
                        data-ft-is-sent-by-user={message.isSentByUser}
                        key={index}
                        dangerouslySetInnerHTML={{
                            __html: message.message,
                        }}
                    ></div>
                )
            })}
        </div>
    )
}
